import { useMemo, useState, type ComponentType, type KeyboardEvent, type ReactNode } from 'react'
import { Badge, Button } from './ui/primitives'
import { NumberField, QuoteActions } from './PricingCalculator'
import {
  BuildingOffice,
  Buildings,
  Check,
  ChevronDown,
  CustomerSupport,
  Headset,
  Info,
  Satellite,
  Sparkles,
  TrendUp,
  Workflow,
  type IconProps,
} from './ui/icons'
import {
  applyCalculatorRules,
  computeFullPricingV2Quote,
  minimumLicensesPerYear,
  computeSubscriptionBreakdown,
  DEFAULT_PRICING_V2_INPUT,
  PRICING_V2,
  type BreakdownLine,
  type IntegrationId,
  type ModuleId,
  type PaymentFrequency,
  type PricingMode,
  type PricingV2Input,
  type SupportTier,
} from '../calculators/pricing-v2'
import { money, number, preciseMoney } from '../lib/format'
import { licensesPerYearPhrase, licenseTermPhrase } from '../lib/license-wording'
import type { TermYears } from '../calculators/enrollment'
import type { WorkspaceQuote } from '../types'

export interface PricingCalculatorV2Props {
  initialInputs?: Partial<PricingV2Input>
  onSave?: (inputs: PricingV2Input) => void
  /** Board-ready PDF generation, same contract as PricingCalculator's. Omitted (as on /pricing-lab) hides the "Generate quote" card entirely. */
  onGenerateQuote?: (inputs: PricingV2Input) => Promise<WorkspaceQuote>
  quotes?: WorkspaceQuote[]
  /** Modules that are always on and cannot be turned off (the partner Pricing preset). */
  lockedModules?: ModuleId[]
  /** False hides the Implementation section and leaves implementation out of every total. Defaults to true. */
  showImplementation?: boolean
  /** False hides the calculator's own heading, for a page that has its own. Defaults to true. */
  showHeading?: boolean
  /** False hides the 90-day opt-out line in the payment schedule. Defaults to true. */
  showOptOut?: boolean
  /**
   * True makes the step 1 "Your price" box show the full per-license price:
   * the license tier plus every selected module, less the Full Platform
   * discount. Defaults to false (tier only).
   */
  rateIncludesModules?: boolean
  /**
   * The pricing modes this calculator offers. With one mode the mode toggle
   * is hidden and the quote always uses that mode. Defaults to both.
   */
  allowedModes?: PricingMode[]
  /**
   * The value (ROI) block, shown directly under the quote summary and above
   * the price breakdown. It gets the live inputs so it always matches the quote.
   */
  valueSummary?: (input: PricingV2Input) => ReactNode
}

const ALL_PRICING_MODES: PricingMode[] = ['license', 'unlimited']

const MODULE_ICONS: Record<ModuleId, ComponentType<IconProps>> = {
  ai_coaching: Sparkles,
  employer_partnerships: BuildingOffice,
  outcomes_pro: TrendUp,
}

const INTEGRATION_ICONS: Record<IntegrationId, ComponentType<IconProps>> = {
  sis: Satellite,
  lms: Workflow,
  crm: BuildingOffice,
}

const SUPPORT_ICONS: Record<SupportTier, ComponentType<IconProps>> = {
  standard: CustomerSupport,
  professional: Headset,
  enterprise: Buildings,
}
const SUPPORT_NAMES: Record<SupportTier, string> = { standard: 'Standard', professional: 'Professional', enterprise: 'Enterprise' }
const SUPPORT_ONE_LINERS: Record<SupportTier, string> = {
  standard: '24/7 AI support and 5 hours a quarter. Most schools need nothing more.',
  professional: 'A success manager, a success plan, and usage reviews.',
  enterprise: 'A dedicated team, an SLA, and custom work.',
}
const SUPPORT_TIERS: SupportTier[] = ['standard', 'professional', 'enterprise']

const MODE_LABELS: Record<PricingMode, string> = { license: 'Per license', unlimited: 'Unlimited' }

/** Short line under the term selector at the top of the right column, one per mode. Shown only on a 1-year term. */
const TERM_CARD_NOTE: Record<PricingMode, string> = {
  license: 'Licenses last longer on a longer agreement.',
  unlimited: 'A longer term lowers the price per student.',
}

/** Pay upfront/pay yearly control, shown only for 2- and 3-year terms (Rod, 2026-09-27). Neither
 * label nor note mentions "license", so the same copy works for both modes. */
const PAYMENT_OPTIONS: Array<{ value: PaymentFrequency; title: string }> = [
  { value: 'upfront', title: 'Pay upfront' },
  { value: 'yearly', title: 'Pay yearly' },
]
const PAYMENT_CARD_NOTE: Record<PaymentFrequency, string> = {
  upfront: 'Pay upfront for the lower multi-year rate.',
  yearly: 'Paying yearly uses the one-year rate.',
}

/** Shortened from the spec's exact copy ("...to your other tools with a no-code builder.") so it stays on one line at 1280px when the price column shows "Included with Full Platform"; kept the meaning. */
const AUTOMATED_WORKFLOWS_DESCRIPTION = 'Connect Prentus to your tools with a no-code builder.'
const AUTOMATED_WORKFLOWS_BULLETS = [
  'Connect the third-party apps your team already uses',
  'Set up webhooks that send Prentus data to other systems when something happens',
  'Automate actions inside Prentus with custom triggers, like assigning a task when a student finishes onboarding',
]
/** With Full Platform, Workflows is included but no integration is bundled free, so the last bullet changes. */
const AUTOMATED_WORKFLOWS_INTEGRATION_BULLET = 'Includes one integration we build and maintain'
const AUTOMATED_WORKFLOWS_FULL_PLATFORM_BULLET = 'With Full Platform, each integration is priced on its own'

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('')
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

/** Charge-line details in License mode read "1,000 licenses x $23"; Unlimited mode never says "license" or exposes the searcher count, so every charge line's detail is replaced with an enrollment-based sub instead. */
function toUnlimitedBreakdownLines(lines: BreakdownLine[], enrollment: number): BreakdownLine[] {
  // Only count-based details ("1,000 licenses x $5") are swapped; a detail like
  // "Includes your first integration (SIS)" never mentions licenses and is kept.
  return lines.map((line) => (line.kind === 'charge' && (!line.detail || /licen|^\d/i.test(line.detail)) ? { ...line, detail: `For ${number(enrollment)} enrolled students` } : line))
}

export function PricingCalculatorV2({ initialInputs, onSave, onGenerateQuote, quotes = [], lockedModules = [], showImplementation = true, showHeading = true, showOptOut = true, rateIncludesModules = false, allowedModes = ALL_PRICING_MODES, valueSummary }: PricingCalculatorV2Props) {
  const lockedKey = lockedModules.join(',')
  const modesKey = allowedModes.join(',')
  const modeOptions = useMemo(() => (modesKey ? modesKey.split(',') as PricingMode[] : ALL_PRICING_MODES), [modesKey])
  // Locked modules, a hidden implementation, and the allowed modes are fixed rules on every quote.
  const rules = useMemo(() => ({ lockedModules: lockedKey ? lockedKey.split(',') as ModuleId[] : [], includeImplementation: showImplementation, allowedModes: modeOptions }), [lockedKey, showImplementation, modeOptions])
  const [rawInput, setInput] = useState<PricingV2Input>(() => ({ ...DEFAULT_PRICING_V2_INPUT, ...initialInputs }))
  // Locked modules stay selected and a hidden implementation stays out of the price.
  const input = useMemo(() => applyCalculatorRules(rawInput, rules), [rawInput, rules])
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})

  function patch(partial: Partial<PricingV2Input>) {
    setInput((current) => ({ ...current, ...partial }))
  }
  function toggleModule(id: ModuleId) {
    if (lockedModules.includes(id)) return
    setInput((current) => ({
      ...current,
      selectedModules: current.selectedModules.includes(id) ? current.selectedModules.filter((item) => item !== id) : [...current.selectedModules, id],
    }))
  }
  function toggleIntegration(id: IntegrationId) {
    setInput((current) => ({
      ...current,
      selectedIntegrations: current.selectedIntegrations.includes(id) ? current.selectedIntegrations.filter((item) => item !== id) : [...current.selectedIntegrations, id],
    }))
  }
  function toggleExpanded(id: string) {
    setExpandedCards((current) => ({ ...current, [id]: !current[id] }))
  }
  /** Selects all three Full Platform modules at once. A no-op once Full Platform is already active. */
  function selectFullPlatform() {
    setInput((current) => {
      const required = PRICING_V2.fullPlatform.requiredModules
      if (required.every((id) => current.selectedModules.includes(id))) return current
      return { ...current, selectedModules: Array.from(new Set([...current.selectedModules, ...required])) }
    })
  }
  function handleFullPlatformKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectFullPlatform()
    }
  }

  const config = PRICING_V2
  const fullPlatform = config.fullPlatform.requiredModules.every((id) => input.selectedModules.includes(id))
  const missingModuleNames = config.modules.filter((module) => config.fullPlatform.requiredModules.includes(module.id) && !input.selectedModules.includes(module.id)).map((module) => module.name)
  const integrationPrice = config.integrations[0]?.recurringPerLicense ?? 2
  // Data APIs are already listed under "Included with every license", so this line only covers
  // the service we build. Without Full Platform the first integration comes with Automated
  // Workflows; with Full Platform every integration is priced.
  const integrationsDescription = input.mode === 'license'
    ? (fullPlatform
        ? `We build and maintain the sync for you. $${integrationPrice}\u00a0per\u00a0license each.`
        : `We build and maintain the sync for you. The first comes with Automated Workflows, then $${integrationPrice}\u00a0per\u00a0license each.`)
    : (fullPlatform
        ? 'We build and maintain the sync for you. Priced for your enrollment.'
        : 'We build and maintain the sync for you. The first comes with Automated Workflows. More are priced for your enrollment.')

  // Single source of truth for the numeric quote: also called server-side by
  // worker/routes/workspace-quotes.ts (computeFullPricingV2Quote in
  // ../calculators/pricing-v2) so the Worker's recompute can never drift
  // from what this UI showed the customer.
  const {
    licenseQuote,
    unlimitedFormula,
    startersQuote,
    anyStarterApplied,
    isZeroedOnboarding,
    activeQuote,
    headline,
    selectedOnboarding,
    paymentSchedule,
    totalContractValue,
    platformTerm,
    oneNumber,
    perLicenseOverTerm,
  } = useMemo(() => computeFullPricingV2Quote(input, config), [input, config])

  // The partner "Your price" box: tier plus selected modules, less the Full Platform discount, per license.
  const fullRatePerLicense = licenseQuote.billedLicenses > 0
    ? (licenseQuote.baseAnnual + licenseQuote.modulesAnnual - licenseQuote.fullPlatformDiscount) / licenseQuote.billedLicenses
    : licenseQuote.tierPrice + licenseQuote.moduleLineItems.reduce((sum, item) => sum + item.price, 0)

  // Scan credits only ever come from Outcomes Pro (or Full Platform); Starters never earns them.
  const activeScanCreditsPerYear = anyStarterApplied ? 0 : activeQuote.scanCreditsPerYear
  const outcomesProActive = activeScanCreditsPerYear > 0
  const creditPriceStandard = config.metered.scanTypes[0].standardRate / config.metered.scanTypes[0].credits
  const creditPricePro = config.metered.scanTypes[0].proRate / config.metered.scanTypes[0].credits

  // Price breakdown (customer-facing summary): Starters shows its own flat-fee line items;
  // License/Unlimited shows the itemized subscription decomposition. Unlimited mode never
  // shows the word "license" or the searcher count, so its charge lines get an
  // enrollment-based sub instead of the license-count detail.
  const subscriptionBreakdownLines: BreakdownLine[] = anyStarterApplied
    ? [
        ...startersQuote.lineItems.map((item): BreakdownLine => (
          item.annual === 0
            ? { label: item.name, amount: 0, kind: 'included', includedValue: 0 }
            : { label: item.name, amount: item.annual, kind: 'charge' }
        )),
        { label: 'Annual subscription', amount: startersQuote.annualSubscription, kind: 'subtotal' },
      ]
    : input.mode === 'unlimited'
      ? toUnlimitedBreakdownLines(computeSubscriptionBreakdown(activeQuote, config), input.enrollment)
      : computeSubscriptionBreakdown(activeQuote, config)

  // The same license wording as the Overview value snapshot: "2,500 licenses over 2 years, 1,250 a year".
  const licenseQuantityLine = [licenseTermPhrase(licenseQuote.billedLicenses, input.termYears), licensesPerYearPhrase(licenseQuote.billedLicenses, input.termYears)].filter(Boolean).join(', ')

  const baseTermDiscountLabel =input.termYears === 3 ? 'Waived with a 3-year term' : '50% off with a 2-year term'
  const onboardingBreakdownLines: BreakdownLine[] = isZeroedOnboarding
    ? []
    : [
        { label: 'Base setup', amount: selectedOnboarding.baseFull, kind: 'charge' },
        ...(selectedOnboarding.termDiscountAmount > 0
          ? [{ label: 'Base setup discount', amount: -selectedOnboarding.termDiscountAmount, kind: 'discount' as const, detail: baseTermDiscountLabel }]
          : []),
        ...selectedOnboarding.addOnLines,
      ]

  return (
    <div className="pv2-root">
      {(showHeading || onSave) && (
        <div className="pv2-heading">
          {showHeading && (
            <div>
              <h2>Prentus pricing calculator v2</h2>
              <p>Build a quote step by step. The summary on the right updates live.</p>
            </div>
          )}
          {onSave && (
            <div className="pv2-heading-actions">
              <Button variant="secondary" compact onClick={() => onSave(input)}>Save pricing</Button>
            </div>
          )}
        </div>
      )}

      {modeOptions.length > 1 && (
        <div className="pv2-top-toggle">
          <SegmentedGroup label="Pricing mode" value={input.mode} onChange={(mode) => patch({ mode })} options={modeOptions.map((mode) => ({ value: mode, title: MODE_LABELS[mode] }))} />
        </div>
      )}

      <div className="pv2-layout">
        <div className="pv2-main">
          <section className="pv2-step">
            <StepHeader step={1} title={input.mode === 'license' ? 'Your licenses' : 'Your enrollment'} description="Tell us who you are and how you pay." />

            {input.mode === 'license' ? (
              <>
                <div className="pv2-license-row">
                  <NumberField label="Licenses per year" value={input.annualLicenses} onChange={(annualLicenses) => patch({ annualLicenses })} />
                  <div className="pv2-rate-box">
                    <span>Your price</span>
                    <div>
                      <strong>{rateIncludesModules ? preciseMoney(fullRatePerLicense) : `$${licenseQuote.tierPrice}`}<small> per license</small></strong>
                    </div>
                  </div>
                </div>

                {input.termYears > 1 && licenseQuote.pooledLicenses > 0 && (
                  <p className="pv2-note">That is {licenseTermPhrase(licenseQuote.billedLicenses, input.termYears)}.</p>
                )}

                <HowItWorks
                  title="How licenses work"
                  bullets={[
                    'You only pay for students who use Prentus. A license is used when a student starts a job search, and it covers them until they are hired or your term ends.',
                    'Licenses pool across your term, so use them any time.',
                    `${number(config.licenseMinimum)} license minimum per year, or across your full term when you pay upfront. Add more any time at your rate.`,
                  ]}
                />

                {input.annualLicenses > 0 && input.annualLicenses < minimumLicensesPerYear(input.termYears, input.payment, config) && !input.starters.programLaunch && (
                  <div className="pv2-program-launch-note">
                    <Info size={16} weight="bold" aria-hidden="true" />
                    <span>Fewer than 500 learners? Program Launch covers up to 250 for $7,500 a year.</span>
                    <Button
                      variant="secondary"
                      compact
                      onClick={() => patch({ starters: { ...input.starters, programLaunch: true, programLearners: input.annualLicenses } })}
                    >
                      Apply Program Launch
                    </Button>
                  </div>
                )}
                {input.starters.programLaunch && (
                  <div className="pv2-program-launch-applied">
                    <Check size={16} weight="bold" aria-hidden="true" />
                    <span>Program Launch applied. Setup included, no separate implementation fee.</span>
                    <Button variant="secondary" compact onClick={() => patch({ starters: { ...input.starters, programLaunch: false } })}>Remove</Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="pv2-license-row pv2-license-row-single">
                  <NumberField label="Total enrollment" value={input.enrollment} onChange={(enrollment) => patch({ enrollment })} />
                </div>
                {input.enrollment > 0 && (
                  <p className="pv2-note">Priced at the {number(unlimitedFormula.enrollmentBand.min)} to {number(unlimitedFormula.enrollmentBand.max)} enrollment band.</p>
                )}

                <HowItWorks
                  title="How it works"
                  bullets={[
                    'Every enrolled student is covered.',
                    'Priced by total enrollment.',
                    'A longer term lowers the price per student.',
                  ]}
                />

                {anyStarterApplied && (
                  <div className="pv2-program-launch-applied">
                    <Check size={16} weight="bold" aria-hidden="true" />
                    <span>A Starter product is applied and is driving this quote instead.</span>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="pv2-checklist ui-card" aria-label="Included features">
            <h3>{input.mode === 'license' ? 'Included with every license' : 'Included for every student'}</h3>
            <div className="pv2-feature-list">
              {config.careerPlatformIncludedFeatures.filter((feature) => !feature.hidden).map((feature) => (
                <div className="pv2-feature-row" key={feature.name}>
                  <div className="pv2-feature-name">
                    <Check size={13} weight="bold" aria-hidden="true" />
                    <strong>{feature.name}</strong>
                    {feature.beta && <Badge tone="neutral">Beta</Badge>}
                  </div>
                  <p>{feature.definition}</p>
                  {feature.beta && (
                    <button
                      type="button"
                      className="pv2-flag-interest"
                      aria-pressed={input.careerExplorationInterest}
                      onClick={() => patch({ careerExplorationInterest: !input.careerExplorationInterest })}
                    >
                      {input.careerExplorationInterest ? 'Interest flagged' : 'Flag interest'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="pv2-step">
            <StepHeader step={2} title="Expansion modules" description="Select all three modules to unlock Full Platform." />
            <div
              className={fullPlatform ? 'pv2-full-platform active' : 'pv2-full-platform'}
              role="button"
              tabIndex={0}
              aria-pressed={fullPlatform}
              aria-label={fullPlatform ? 'Full Platform active. All three modules are selected.' : `Select Full Platform: add ${joinNames(missingModuleNames)}`}
              onClick={selectFullPlatform}
              onKeyDown={handleFullPlatformKeyDown}
            >
              <div className="pv2-full-platform-copy">
                <strong>Full Platform</strong>
                <span>{fullPlatform ? 'Active. All three modules are selected.' : `Add ${joinNames(missingModuleNames)} to unlock it.`}</span>
              </div>
              <ul>
                <li><Check size={13} weight="bold" aria-hidden="true" />10% off the annual subscription</li>
                <li><Check size={13} weight="bold" aria-hidden="true" />Professional support included</li>
                <li><Check size={13} weight="bold" aria-hidden="true" />{input.mode === 'license' ? `${config.fullPlatform.scanCreditsPerLicense} scan credits per license instead of ${config.outcomesProCredits.standardPerLicense}` : '50% more scan credits'}</li>
                <li><Check size={13} weight="bold" aria-hidden="true" />Automated Workflows included</li>
              </ul>
            </div>
            <div className="pv2-select-list">
              {config.modules.map((module) => {
                const lineItem = activeQuote.moduleLineItems.find((item) => item.id === module.id)
                const priceDisplay = input.mode === 'license'
                  ? `$${module.price} per license`
                  : `${money(lineItem?.annual ?? activeQuote.billedLicenses * module.price)} a year`
                const bullets = module.id === 'outcomes_pro'
                  ? [...module.includes.slice(0, 3), input.mode === 'license' ? module.includes[3]! : 'Prepaid scan credits, then 50% off every scan']
                  : module.includes
                const locked = lockedModules.includes(module.id)
                return (
                  <SelectableCard
                    key={module.id}
                    id={`module-${module.id}`}
                    as="checkbox"
                    icon={MODULE_ICONS[module.id]}
                    selected={input.selectedModules.includes(module.id)}
                    disabled={locked}
                    name={module.name}
                    description={module.description}
                    price={priceDisplay}
                    priceNote={locked ? 'Always included' : undefined}
                    bullets={bullets}
                    expanded={Boolean(expandedCards[`module-${module.id}`])}
                    onToggleExpanded={() => toggleExpanded(`module-${module.id}`)}
                    onToggle={() => toggleModule(module.id)}
                  />
                )
              })}
              <SelectableCard
                id="automated-workflows"
                as="checkbox"
                icon={Workflow}
                selected={fullPlatform || activeQuote.workflowsAnnual > 0}
                disabled={fullPlatform || activeQuote.workflowsForcedByIntegration}
                name="Automated Workflows"
                description={AUTOMATED_WORKFLOWS_DESCRIPTION}
                bullets={[...AUTOMATED_WORKFLOWS_BULLETS, fullPlatform ? AUTOMATED_WORKFLOWS_FULL_PLATFORM_BULLET : AUTOMATED_WORKFLOWS_INTEGRATION_BULLET]}
                expanded={Boolean(expandedCards['automated-workflows'])}
                onToggleExpanded={() => toggleExpanded('automated-workflows')}
                price={fullPlatform ? 'Included with Full Platform' : input.mode === 'license' ? `$${config.automatedWorkflows.pricePerLicense} per license` : `${money(activeQuote.billedLicenses * config.automatedWorkflows.pricePerLicense)} a year`}
                priceNote={!fullPlatform && activeQuote.workflowsForcedByIntegration ? 'Added with your first integration' : undefined}
                onToggle={() => patch({ automatedWorkflows: !input.automatedWorkflows })}
              />
            </div>
          </section>

          <section className="pv2-step">
            <StepHeader
              step={3}
              title="Integrations"
              description={integrationsDescription}
            />
            <div className="pv2-select-list">
              {config.integrations.map((integration) => {
                const lineItem = activeQuote.integrationLineItems.find((item) => item.id === integration.id)
                const included = Boolean(lineItem?.includedWithWorkflows)
                const priceDisplay = included
                  ? 'Included with Automated Workflows'
                  : input.mode === 'license'
                    ? `$${integration.recurringPerLicense} per license`
                    : `${money(activeQuote.billedLicenses * integration.recurringPerLicense)} a year`
                return (
                  <SelectableCard
                    key={integration.id}
                    id={`integration-${integration.id}`}
                    as="checkbox"
                    icon={INTEGRATION_ICONS[integration.id]}
                    selected={input.selectedIntegrations.includes(integration.id)}
                    name={integration.name}
                    description={integration.description}
                    price={priceDisplay}
                    draft={integration.draft}
                    onToggle={() => toggleIntegration(integration.id)}
                  />
                )
              })}
            </div>
          </section>

          <section className="pv2-step">
            <StepHeader step={4} title="Support" description={input.mode === 'license' ? 'Standard support is included with every license.' : 'Standard support is included with every plan.'} />
            <div className="pv2-select-list" role="radiogroup" aria-label="Support tier">
              {SUPPORT_TIERS.map((tier) => {
                const applied = activeQuote.appliedSupportTier === tier
                const standardLockedByFullPlatform = tier === 'standard' && fullPlatform
                const priceDisplay = tier === 'standard'
                  ? (standardLockedByFullPlatform ? 'Not available' : 'Included')
                  : tier === 'professional'
                    ? (fullPlatform ? 'Included with Full Platform' : input.mode === 'license' ? `$${config.support.professional.perLicensePerYear} per license` : `${money(activeQuote.billedLicenses * config.support.professional.perLicensePerYear)} a year`)
                    : `${money(activeQuote.enterpriseSupportAnnual)} a year`
                return (
                  <SelectableCard
                    key={tier}
                    id={`support-${tier}`}
                    as="radio"
                    icon={SUPPORT_ICONS[tier]}
                    selected={applied}
                    disabled={standardLockedByFullPlatform}
                    name={SUPPORT_NAMES[tier]}
                    description={standardLockedByFullPlatform ? 'Full Platform includes Professional support.' : SUPPORT_ONE_LINERS[tier]}
                    bullets={config.support[tier].includes}
                    expanded={Boolean(expandedCards[`support-${tier}`])}
                    onToggleExpanded={() => toggleExpanded(`support-${tier}`)}
                    price={priceDisplay}
                    onToggle={() => patch({ supportTier: tier })}
                  />
                )
              })}
            </div>
            <p className="pv2-note">Extra hours are {money(config.support.overagePerHour)} each, reported {config.support.overageReportingCadence}.</p>
          </section>

          <section className="pv2-metered ui-card" aria-labelledby="pv2-metered-title">
            <h3 id="pv2-metered-title">Scan credits</h3>
            <p>Outcomes scans use credits. A credit is {preciseMoney(creditPriceStandard)}, or {preciseMoney(creditPricePro)} with Outcomes Pro. Only verified results are charged.</p>
            {outcomesProActive ? (
              <p className="pv2-metered-included">Your plan includes {number(activeScanCreditsPerYear)} scan credits a year. Extra credits are {preciseMoney(creditPricePro)} each.</p>
            ) : (
              <p className="pv2-metered-included">Outcomes Pro includes prepaid credits and halves the price of every extra credit.</p>
            )}
            <ul className="pv2-metered-list">
              {config.metered.scanTypes.map((scanType) => (
                <li key={scanType.id}>
                  <span>{scanType.name}</span>
                  <span className="pv2-metered-rate">
                    <span className="pv2-metered-amounts">{scanType.credits} {scanType.credits === 1 ? 'credit' : 'credits'}</span>
                    <small>{outcomesProActive ? `${preciseMoney(scanType.proRate)} with Outcomes Pro` : `${preciseMoney(scanType.standardRate)}, or ${preciseMoney(scanType.proRate)} with Outcomes Pro`}</small>
                  </span>
                </li>
              ))}
              {config.metered.comingSoon.map((label) => (
                <li key={label}>
                  <span>{label}</span>
                  <span className="pv2-metered-rate"><small>Coming soon</small></span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* With a value block the column is taller than the screen, so it scrolls with the page: a sticky column would hide the value block until the end. */}
        <aside className={valueSummary ? 'pv2-aside pv2-aside--static' : 'pv2-aside'}>
          <section className="quote-summary pv2-summary" aria-label="Quote summary">
            <div className="pv2-summary-term">
              <SegmentedGroup
                label="Agreement term"
                value={input.termYears}
                onChange={(termYears) => patch({ termYears })}
                options={([1, 2, 3] as TermYears[]).map((term) => ({ value: term, title: `${term} year${term > 1 ? 's' : ''}` }))}
              />
              {input.termYears > 1 && (
                <div className="pv2-payment-toggle">
                  <SegmentedGroup
                    label="Payment"
                    value={input.payment}
                    onChange={(payment) => patch({ payment })}
                    options={PAYMENT_OPTIONS}
                  />
                </div>
              )}
              <p className="pv2-term-card-note">{input.termYears > 1 ? PAYMENT_CARD_NOTE[input.payment] : TERM_CARD_NOTE[input.mode]}</p>
            </div>

            <div className="pv2-total-row">
              {input.mode === 'license' && !anyStarterApplied && (
                <div className="pv2-per-license-highlight pv2-summary-primary">
                  <span className="pv2-total-kicker">Per license</span>
                  <strong className="pv2-total-figure">{preciseMoney(perLicenseOverTerm)}</strong>
                  <span className="pv2-total-term">Each license covers a student until hired or the term ends</span>
                </div>
              )}
              {input.mode === 'unlimited' && !anyStarterApplied && (
                <div className="pv2-per-license-highlight pv2-summary-primary">
                  <span className="pv2-total-kicker">Per enrolled student</span>
                  <strong className="pv2-total-figure">{preciseMoney(oneNumber.perEnrolledStudentPerYear ?? 0)}</strong>
                  <span className="pv2-total-term">A year, for each of {number(input.enrollment)} enrolled students</span>
                </div>
              )}

              <div className="pv2-per-license-highlight pv2-summary-secondary">
                <span className="pv2-total-kicker">{input.mode === 'unlimited' && !anyStarterApplied ? 'Per year' : 'Annual subscription'}</span>
                <strong className="pv2-total-figure">{money(headline.annualSubscription)}</strong>
              </div>

              <div className="pv2-total-highlight">
                <span className="pv2-total-kicker">Total you pay</span>
                <strong className="pv2-total-figure">{money(totalContractValue)}</strong>
                <span className="pv2-total-term">
                  {input.mode === 'license' && !anyStarterApplied
                    ? licenseQuantityLine
                    : `${input.termYears}-year contract`}
                </span>
                {showImplementation && <div className="pv2-total-detail-list">
                  <div className="pv2-onboarding-row">
                    <span>Implementation</span>
                    {isZeroedOnboarding ? (
                      <strong>{input.renewalQuote ? 'Renewal, no fee' : 'Included'}</strong>
                    ) : selectedOnboarding.termDiscountAmount > 0 ? (
                      <span className="pv2-onboarding-value">
                        <s>{money(selectedOnboarding.baseFull)}</s>
                        <strong>{selectedOnboarding.baseDue === 0 ? 'Waived' : money(selectedOnboarding.baseDue)}</strong>
                        {selectedOnboarding.addOnLines.length > 0 && (
                          <small>plus {money(selectedOnboarding.addOnLines.reduce((sum, line) => sum + line.amount, 0))} in add-ons</small>
                        )}
                      </span>
                    ) : (
                      <strong>{selectedOnboarding.due === 0 ? 'Waived' : money(selectedOnboarding.due)}</strong>
                    )}
                  </div>
                </div>}
              </div>
            </div>
            <div className="payment-schedule">
              <h3>Payment schedule</h3>
              {/* The retained line is the 90-day opt-out. */}
              {paymentSchedule.filter((line) => showOptOut || !line.retained).map((line) => (
                <div className={line.retained ? 'schedule-line opt-out' : 'schedule-line'} key={`${line.label}-${line.timing}`}>
                  <div><strong>{line.label}</strong><span>{line.timing}</span></div>
                  {/* A retained line with nothing kept (no implementation fee) shows no "$0 kept". */}
                  {!(line.retained && line.amount === 0) && (
                    <b className={line.originalAmount !== undefined ? 'schedule-amount discounted' : 'schedule-amount'}>
                      {line.originalAmount !== undefined && <s>{money(line.originalAmount)}</s>}
                      <span>{line.retained ? `${money(line.amount)} kept` : money(line.amount)}</span>
                    </b>
                  )}
                </div>
              ))}
            </div>
          </section>

          {valueSummary?.(input)}

          {onGenerateQuote && (
            <QuoteActions
              inputs={input as unknown as Record<string, unknown>}
              onGenerate={(inputs) => onGenerateQuote(inputs as unknown as PricingV2Input)}
              quotes={quotes}
            />
          )}

          <section className="pv2-breakdown ui-card" aria-label="Price breakdown">
            <h3>Price breakdown</h3>
            <div className="pv2-breakdown-group">
              {subscriptionBreakdownLines.map((line) => <BreakdownRow key={line.label} line={line} />)}
            </div>
            {showImplementation && (
              <div className="pv2-breakdown-group">
                <h4>Implementation</h4>
                {isZeroedOnboarding
                  ? <p className="pv2-note">{input.renewalQuote ? 'Renewal, no implementation fee.' : 'Setup included, no separate implementation fee.'}</p>
                  : <>
                      {onboardingBreakdownLines.map((line, index) => <BreakdownRow key={`${line.label}-${index}`} line={line} />)}
                      <BreakdownRow line={{ label: 'Implementation total', amount: selectedOnboarding.due, kind: 'subtotal' }} />
                    </>}
              </div>
            )}
            <div className="pv2-breakdown-group">
              <BreakdownRow line={{ label: 'Contract total', amount: totalContractValue, kind: 'subtotal' }} />
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

function StepHeader({ step, title, description }: { step: number; title: string; description: string }) {
  return (
    <div className="pv2-step-header">
      <span className="pv2-step-number">{step}</span>
      <div><h3>{title}</h3><p>{description}</p></div>
    </div>
  )
}

function SegmentedGroup<T extends string | number>({ label, value, options, onChange }: { label: string; value: T; options: Array<{ value: T; title: string }>; onChange: (value: T) => void }) {
  return (
    <div className="pv2-term-options" role="group" aria-label={label}>
      {options.map((option) => (
        <button type="button" key={String(option.value)} className={value === option.value ? 'pv2-term-option active' : 'pv2-term-option'} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
          <strong>{option.title}</strong>
        </button>
      ))}
    </div>
  )
}

function HowItWorks({ title, bullets }: { title: string; bullets: string[] }) {
  return (
    <div className="pv2-how-it-works">
      <h4>{title}</h4>
      <ul className="pv2-check-list">
        {bullets.map((item) => (
          <li key={item}><Check size={13} weight="bold" aria-hidden="true" /><span>{item}</span></li>
        ))}
      </ul>
    </div>
  )
}

function BreakdownRow({ line }: { line: BreakdownLine }) {
  const isNegative = line.amount < 0
  if (line.kind === 'note') {
    return (
      <div className="pv2-breakdown-row pv2-breakdown-note">
        <div>
          <span>{line.label}</span>
          {line.detail && <small>{line.detail}</small>}
        </div>
      </div>
    )
  }
  return (
    <div className={`pv2-breakdown-row pv2-breakdown-${line.kind}`}>
      <div>
        <span>{line.label}</span>
        {line.detail && <small>{line.detail}</small>}
      </div>
      {line.kind === 'included'
        ? <b><span>Included</span>{line.includedValue !== undefined && line.includedValue > 0 && <small>(${number(line.includedValue)} value)</small>}</b>
        : <b>{isNegative && '-'}{money(Math.abs(line.amount))}</b>}
    </div>
  )
}

/**
 * Click-to-select card used for modules, Automated Workflows, integrations, and support
 * tiers. The whole card is the control (click, or Enter/Space when focused); a selected
 * card shows a small check in the bottom-right corner. When `bullets` is given, a
 * "What's included" toggle reveals them without affecting selection (its click stops
 * propagation so it never toggles the card).
 */
function SelectableCard({
  id,
  as,
  icon: Icon,
  selected,
  name,
  description,
  price,
  priceNote,
  bullets,
  badge,
  draft = false,
  disabled = false,
  expanded = false,
  onToggleExpanded,
  onToggle,
}: {
  id: string
  as: 'checkbox' | 'radio'
  icon: ComponentType<IconProps>
  selected: boolean
  name: string
  description: string
  price: string
  priceNote?: string
  bullets?: string[]
  badge?: string
  draft?: boolean
  disabled?: boolean
  expanded?: boolean
  onToggleExpanded?: () => void
  onToggle: () => void
}) {
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }
  return (
    <div
      className={`pv2-select-card${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
      role={as}
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onToggle()}
      onKeyDown={handleKeyDown}
    >
      <span className="pv2-select-card-head">
        <span className="pv2-select-card-icon"><Icon size={21} aria-hidden="true" /></span>
        <span className="pv2-select-card-copy">
          <strong>{name}{draft && <Badge tone="warning" className="pv2-draft-badge">Draft</Badge>}</strong>
          <span>{description}</span>
        </span>
        <span className="pv2-select-card-price">
          <b>{price}</b>
          {priceNote && <small>{priceNote}</small>}
        </span>
      </span>
      {badge && <Badge tone="success" className="pv2-option-badge">{badge}</Badge>}
      {bullets && bullets.length > 0 && (
        <>
          <button
            type="button"
            className="pv2-expand-toggle"
            aria-expanded={expanded}
            aria-controls={`${id}-bullets`}
            onClick={(event) => { event.stopPropagation(); onToggleExpanded?.() }}
          >
            <span>{expanded ? 'Hide details' : "What's included"}</span>
            <ChevronDown size={13} weight="bold" aria-hidden="true" />
          </button>
          {expanded && (
            <ul className="pv2-check-list pv2-select-card-bullets" id={`${id}-bullets`}>
              {bullets.map((item) => (
                <li key={item}><Check size={13} weight="bold" aria-hidden="true" /><span>{item}</span></li>
              ))}
            </ul>
          )}
        </>
      )}
      <span className="pv2-select-card-check" aria-hidden="true"><Check size={12} weight="bold" /></span>
    </div>
  )
}
